
import { Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { record_type, lambda_type, int32_type, array_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { item as item_1, getSubArray, copyTo, fill } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { min, max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";

/**
 * A store of 'T addressed by the index each item was stamped with when it was added.
 * 
 * Mutable, deliberately, and build-scoped: see docs/mutableState.md. Nothing outside the module
 * should write `Items` or `Count`.
 */
export class LookupArray$1 extends Record {
    constructor(Items, Count, GetIndex, AddIndexStamp, MaxIncrement) {
        super();
        this.Items = Items;
        this.Count = (Count | 0);
        this.GetIndex = GetIndex;
        this.AddIndexStamp = AddIndexStamp;
        this.MaxIncrement = (MaxIncrement | 0);
    }
}

export function LookupArray$1_$reflection(gen0) {
    return record_type("LookupArray.LookupArray`1", [gen0], LookupArray$1, () => [["Items", array_type(gen0)], ["Count", int32_type], ["GetIndex", lambda_type(gen0, int32_type)], ["AddIndexStamp", lambda_type(gen0, lambda_type(int32_type, gen0))], ["MaxIncrement", int32_type]]);
}

/**
 * A store of `capacity` items before it has to grow, extending by at most `maxIncrement` slots
 * at a time once it does.
 * 
 * The two functions are given separately rather than as a `Lens<'T,int>` so that the caller
 * chooses what stamping costs. A lens setter is a record copy, and the simulator's use of this
 * stores a 24-field record created hundreds of thousands of times per build - while that record
 * is `[<ReferenceEquality>]` and already carries mutable fields, so an index written in place
 * costs nothing and keeps the object identity the simulator relies on:
 * 
 * // in place - no copy, the same object back. The store works in bare ints; a caller whose
 * // index is a wrapped one unwraps at this boundary and nowhere else.
 * LookupArray.create (fun fc -> fcToInt fc.Index) (fun fc i -> fc.Index <- FastCompIndex i; fc) n maxInc
 * // through a lens, where the type is immutable
 * LookupArray.create (Optic.get index_) (fun t i -> Optic.set index_ i t) n maxInc
 * 
 * It also keeps Optics out of this file, which is why it can sit anywhere in compile order.
 */
export function create(getIndex, addIndexStamp, capacity, maxIncrement) {
    return new LookupArray$1(fill(new Array(max(0, capacity)), 0, max(0, capacity), null), 0, getIndex, addIndexStamp, max(1, maxIncrement));
}

function grow(store) {
    const old = store.Items.length | 0;
    const increment = max(4, min(old, store.MaxIncrement)) | 0;
    const bigger = fill(new Array(old + increment), 0, old + increment, null);
    copyTo(store.Items, 0, bigger, 0, store.Count);
    store.Items = bigger;
}

/**
 * Add an item, stamping it with the index it is stored at.
 * 
 * **Use the returned item, never the argument.** With an in-place stamp the two are the same
 * object and with a lens they are not, so `addItem x store |> ignore` is correct in one flavour
 * and silently wrong in the other. The compiler cannot tell them apart.
 */
export function addItem(item, store) {
    if (store.Count >= store.Items.length) {
        grow(store);
    }
    const stamped = store.AddIndexStamp(item, store.Count);
    store.Items[store.Count] = stamped;
    store.Count = ((store.Count + 1) | 0);
    return stamped;
}

/**
 * Replace the item at an index already in the store. Does not re-stamp: the index is the one
 * the item was given when it was added.
 */
export function updateItem(i, item, store) {
    if ((i < 0) ? true : (i >= store.Count)) {
        toFail(`LookupArray.updateItem: index ${i} is outside the ${store.Count} items stored`);
    }
    store.Items[i] = item;
}

/**
 * A copy of what is stored, truncated to Count - so it never exposes an unwritten slot.
 */
export function toArray(store) {
    return getSubArray(store.Items, 0, store.Count);
}

/**
 * Apply a function to every stored item and its index, in creation order.
 */
export function iteri(f, store) {
    for (let i = 0; i <= (store.Count - 1); i++) {
        f(i, item_1(i, store.Items));
    }
}

